import { css, cx } from '@repo/pandacss/styled-system/css';
import { flex } from '@repo/pandacss/styled-system/patterns';
import { BankingSubProductTypeEnum } from '@repo/supergraph/marketplace/enums';
import { BestOfAwardsRoundupBanner } from '../../BestOfAwards/Roundup';
import { getBestOfAwardsConfigForOffer } from '../../BestOfAwards/utils';
import { CtaName } from './CtaName';
import { Driver } from './Driver';
import { ImageCtaReviewSection } from './ImageCtaReviewSection';
import { OurTake } from './OurTake';
import { SpotlightBanner } from './Spotlight/SpotlightBanner';
import { SpotlightTitle } from './Spotlight/SpotlightTitle';
import type { ProductCardProps } from './types';

const cardBaseStyles = css({
  borderTopStyle: 'solid',
  borderRightStyle: 'solid',
  borderBottomStyle: 'solid',
  borderLeftStyle: 'solid',
  borderTopWidth: 'medium',
  borderRightWidth: 'medium',
  borderBottomWidth: 'medium',
  borderLeftWidth: 'medium',
});

const cardSpotlightStyles = css({
  borderTopColor: 'green.darkest',
  borderRightColor: 'green.darkest',
  borderBottomColor: 'green.darkest',
  borderLeftColor: 'green.darkest',
  borderBottomLeftRadius: '[10px]',
  borderBottomRightRadius: '[10px]',
  borderTopLeftRadius: '[0]',
  borderTopRightRadius: '[0]',
  // Clip the OurTake accordion (full-width background / square corners) to the rounded bottom
  overflowX: 'hidden',
  overflowY: 'hidden',
});

const cardDefaultStyles = css({
  borderTopColor: 'neutral.lighter',
  borderRightColor: 'neutral.lighter',
  borderBottomColor: 'neutral.lighter',
  borderLeftColor: 'neutral.lighter',
});

const cardContentBaseStyles = css({
  paddingInlineStart: { base: '4', md: '7' },
  paddingInlineEnd: { base: '4', md: '7' },
  paddingBottom: {
    base: 'var(--card-pb)',
    md: '4',
  },
});

const cardContentSpotlightMarginStyles = css({
  marginTop: { base: '4', md: '6' },
});

const cardContentDefaultMarginStyles = css({
  marginTop: '4',
});

export const ProductCard = ({
  ctaAnalytics,
  bottomLine,
  cta,
  deviceType,
  displayName,
  drivers,
  image,
  monetizable,
  pageType = 'roundup',
  shouldApplyStylingUpdates = false,
  starRating,
  starRatingTooltip,
  reviewLink,
  isOurTakeExpanded,
  productType,
  subProductType,
  pros,
  cons,
  slug,
  spotlightHeading,
  spotlightDetails,
  authorQuote,
  oneYearApyEntity,
  threeYearApyEntity,
  fiveYearApyEntity,
  hideBestOfAwardsBanner = false,
}: ProductCardProps) => {
  const { config: bestOfAwardsConfig, hasBestOfAward } = getBestOfAwardsConfigForOffer(
    ctaAnalytics.offerId,
    slug,
    pageType,
    deviceType,
    ctaAnalytics.institutionId,
    subProductType,
  );
  const shouldDisplayBestOfAwardsBanner = (hasBestOfAward ?? false) && !hideBestOfAwardsBanner;

  const shouldRenderOurTake = Boolean(bottomLine);

  const isSpotlight = Boolean(spotlightHeading && spotlightDetails);

  const cardClassName = cx(cardBaseStyles, isSpotlight ? cardSpotlightStyles : cardDefaultStyles);

  return (
    <>
      <SpotlightTitle title={spotlightHeading} details={spotlightDetails} />
      <div className={cardClassName} data-testid="product-card">
        <div
          className={cx(
            cardContentBaseStyles,
            isSpotlight ? cardContentSpotlightMarginStyles : cardContentDefaultMarginStyles,
          )}
          style={
            {
              '--card-pb': shouldRenderOurTake ? '0' : '1rem',
            } as React.CSSProperties
          }
        >
          <SpotlightBanner title={spotlightHeading} details={spotlightDetails} />
          {shouldDisplayBestOfAwardsBanner && bestOfAwardsConfig ? (
            <BestOfAwardsRoundupBanner variant="card" className={css({ marginBottom: '4' })}>
              {bestOfAwardsConfig.awardText}
            </BestOfAwardsRoundupBanner>
          ) : null}
          <div className={css({ marginBottom: '4' })}>
            {cta ? (
              <CtaName cta={cta} ctaAnalytics={ctaAnalytics} displayName={displayName} />
            ) : (
              <p className={css({ textStyle: 'boxHeadline', fontWeight: 'bold' })}>{displayName}</p>
            )}
          </div>
          <div
            className={css({
              display: 'flex',
              flexDirection: { base: 'column', md: 'row' },
            })}
          >
            <ImageCtaReviewSection
              ctaAnalytics={ctaAnalytics}
              cta={cta}
              displayName={displayName}
              image={image}
              monetizable={monetizable}
              shouldApplyStylingUpdates={shouldApplyStylingUpdates}
              starRating={starRating}
              starRatingTooltip={starRatingTooltip}
              reviewLink={reviewLink}
            />
            <div
              className={flex({
                flexDirection: 'column',
                marginTop: { base: '4', md: '0' },
                grow: '1',
                shrink: '1',
                basis: '[0%]',
              })}
            >
              {drivers.map((driver, index) => (
                <Driver
                  key={driver.label}
                  driver={driver}
                  renderCdApys={
                    subProductType === BankingSubProductTypeEnum.CD && driver.id?.includes('apy')
                  }
                  borderBottom={index !== drivers.length - 1}
                  oneYearApyEntity={oneYearApyEntity}
                  threeYearApyEntity={threeYearApyEntity}
                  fiveYearApyEntity={fiveYearApyEntity}
                />
              ))}
            </div>
          </div>
        </div>
        <OurTake
          bottomLine={bottomLine}
          isDefaultExpanded={isOurTakeExpanded}
          productType={productType}
          pros={pros}
          cons={cons}
          cta={cta}
          ctaAnalytics={ctaAnalytics}
          authorQuote={authorQuote}
          displayName={displayName}
        />
      </div>
    </>
  );
};
